import { APP_INITIALIZER, Injector, LOCALE_ID, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AccountComponent } from './pages/account/account.component';
import { ForbiddenComponent } from './pages/forbidden/forbidden.component';
import { UnauthorizedComponent } from './pages/unauthorized/unauthorized.component';
import { AuthConfigModule } from './auth-config.module';
import { filter, take } from 'rxjs';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { SharedModule } from './@shared/shared.module';
import { NotFoundComponent } from './pages/not-found/not-found.component';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { HTTP_INTERCEPTORS, HttpClient } from '@angular/common/http';
import { SubscriptionsComponent } from './pages/subscriptions/subscriptions.component';
import {
  PARKBASE_SERVICE_STORAGE,
  ParkbaseStorageService,
} from './@shared/services/parkbaseStorage.service';
import { LOCAL_STORAGE } from 'ngx-webstorage-service';
import { ProfileComponent } from './pages/profile/profile.component';
import { ConfigService } from './@shared/services/config.service';
import { CoreModule, ErrorHandlerInterceptor, LogService } from './@core';
import { ComponentService } from './@shared/utils/component.service';
import { LogPublishersService } from './@core/logging/log-publisher.service';
import { NbRoleProvider, NbSecurityModule } from '@nebular/security';
import { RoleProvider } from './@shared/services/role.provider';
import { DirectAccessGuard } from './auth/guards/direct-access.guard';
import { EventTypes, PublicEventsService } from 'angular-auth-oidc-client';
import { NbActionsModule, NbAlertModule, NbThemeModule, NbUserModule } from '@nebular/theme';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { StatusPageComponent } from './pages/status-page/status-page.component';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { ShellModule } from './shell/shell.module';
import { AppInjector } from './@shared/services/app.injector';
import { ScrollingModule } from '@angular/cdk/scrolling';
// import { FacilitiesComponent } from './pages/dashboard/components/facilities/facilities.component';
// import { FacilityComponent } from './pages/dashboard/components/facilities/facility/facility.component';
// import { BarriersComponent } from './pages/dashboard/components/barriers/barriers.component';
// import { IntercomComponent } from './pages/dashboard/components/intercom/intercom.component';
// import { SearchComponent } from './pages/dashboard/components/search/search.component';
import { ConfirmDialogComponent } from './pages/status-page/components/totals/confirm-dialog/confirm-dialog.component';
import { ProfileDialogComponent } from './pages/profile/profile-dialog/profile-dialog.component';
import { IdentificationInputComponent } from './pages/identification/identification-input/identification-input.component';
import { IdentificationValidationComponent } from './pages/identification/identification-validation/identification-validation.component';
import { IdentificationScannerComponent } from './pages/identification/identification-scanner/identification-scanner.component';
import { StatusPageModule } from './pages/status-page/status-page.module';
import { registerLocaleData } from '@angular/common';
import localeNl from '@angular/common/locales/nl';
import localeEnUs from '@angular/common/locales/en';
import localeEnGB from '@angular/common/locales/en-GB';
import { LicenseplateSettingsComponent } from './pages/settings/licenseplate-settings/licenseplate-settings.component';
import { LicenseplateSettingsModule } from './pages/settings/licenseplate-settings/licenseplate-settings.module';
import { Router } from '@angular/router';
import { OidcClientNotification } from 'angular-auth-oidc-client/lib/public-events/notification';
import { PageTemplateComponent } from './pages/page-template/page-template.component';

registerLocaleData(localeNl);
registerLocaleData(localeEnUs);
registerLocaleData(localeEnGB);

/**
 * ACL
 */
const aclConfig = {
  accessControl: {
    ConfigurationManagers: {
      manage: ['apiClient', 'apiAccess', 'accessLprSettings'],
    },
    ConfigurationUsers: {
      manage: ['bulkTransactionClose'],
    },
    Cashiers: {
      manage: ['identificationValidation'],
    },
    Operators: {
      manage: ['statusPage'],
    },
  },
};

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, '/assets/i18n/', `.json?v=${new Date().getTime()}`);
}

export function ConfigLoader(injector: Injector) {
  return () => injector.get(ConfigService).loadConfiguration();
}

@NgModule({
  declarations: [
    AppComponent,
    AccountComponent,
    ForbiddenComponent,
    UnauthorizedComponent,
    NotFoundComponent,
    SubscriptionsComponent,
    ProfileComponent,
    StatusPageComponent,
    // LayoutItemDirective,
    // FacilitiesComponent,
    // FacilityComponent,
    // BarriersComponent,
    // IntercomComponent,
    // SearchComponent,
    ConfirmDialogComponent,
    ProfileDialogComponent,
    IdentificationInputComponent,
    IdentificationValidationComponent,
    IdentificationScannerComponent,
    LicenseplateSettingsComponent,
    PageTemplateComponent,
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    ScrollingModule,
    AppRoutingModule,
    AuthConfigModule,
    // TourNgxBootstrapModule.forRoot(),
    NbSecurityModule.forRoot(aclConfig),
    NbThemeModule.forRoot({ name: 'parkbaseDark' }),
    TranslateModule.forRoot({
      defaultLanguage: 'en-US',
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    NgbModule,
    SharedModule.forRoot(),
    // GridsterModule,
    CoreModule,
    ShellModule,
    StatusPageModule,
    NbAlertModule,
    LicenseplateSettingsModule,
    NbActionsModule,
    NbUserModule,
  ],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: ConfigLoader,
      deps: [Injector],
      multi: true,
    },
    DirectAccessGuard,
    ComponentService,
    LogService,
    LogPublishersService,
    ParkbaseStorageService,
    {
      provide: PARKBASE_SERVICE_STORAGE,
      useExisting: LOCAL_STORAGE,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorHandlerInterceptor,
      multi: true,
    },
    { provide: LOCALE_ID, useValue: 'nl' },
    { provide: NbRoleProvider, useClass: RoleProvider },
  ],
  bootstrap: [AppComponent],
  exports: [],
})
export class AppModule {
  constructor(
    private readonly eventService: PublicEventsService,
    private router: Router,
    injector: Injector,
    logger: LogService
  ) {
    AppInjector.setInjector(injector);

    setTimeout(() => {
      this.eventService
        .registerForEvents()
        .pipe(take(2))
        .subscribe((e: OidcClientNotification<any>) => {
          if (e.type === EventTypes.ConfigLoaded) {
            logger.info('Auth ConfigLoaded!', e.type === EventTypes.ConfigLoaded);
          }
        });

      this.eventService
        .registerForEvents()
        .pipe(filter((notification: any) => notification.type === EventTypes.ConfigLoadingFailed))
        .subscribe((config: any) => {
          logger.warning('Auth ConfigLoadingFailed', EventTypes.ConfigLoadingFailed);
          router.navigateByUrl('/unauthorized');
        });
    }, 5000);
  }
}
